'use client';

import { useStreamingStore } from '@/store/streaming';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { useRouter } from '@/navigation';
import { useCurrentProperties } from '@/hooks/query';
import { useAppearanceStore } from '@/store/appearance';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';
import { ViewEnum } from '@/types';
import { Breakpoint } from '@/features/breakpoint';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useGetEnableMatchList } from '@/hooks/query/use-promotion-data';
import { useGoldenDuelSetting } from '@/hooks/query/golden-duel';

interface MatchEventWrapperProps {
	market: string;
	leagueId: number;
	matchId: number;
	sport: number;
	children: React.ReactNode;
}

export const MatchEventWrapper = ({
	market,
	leagueId,
	matchId,
	sport,
	children
}: MatchEventWrapperProps) => {
	const [
		setStreamingStatus,
		streamingType,
		streamingPluginUrl,
		setStreamingPluginUrl,
	] = useStreamingStore(
		state => [
			state.setStreamingStatus,
			state.streamingType,
			state.streamingPluginUrl,
			state.setStreamingPluginUrl,
		],
	);
	const view = useAppearanceStore(state => state.view);
	const { breakpoint } = useBreakpoint();
	const router = useRouter();
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const { data: jackpotEnableMatchList } = useGetEnableMatchList();
	const { isGoldenDuelSupported } = useGoldenDuelSetting();
	const { eventMode, eventName } = useCurrentProperties();
	const singleMatchHref = eventMode !== 'M0' ? `/world/${eventName}/${sport}/${market}/${leagueId}/${matchId}` : matchEquals(market)
		.on('l', () => `/live/${sport}/${leagueId}/${matchId}`)
		.otherwise(() => `/sports/${sport}/${market}/${leagueId}/${matchId}`);

	const setPromotionKafka = () => {
		// jackpot
		if (siteConfig?.EnableSingleMatchJackpot && (jackpotEnableMatchList?.find(x => x.MatchId == matchId) ?? false)) {
			setKafkaNormal({
				note: KafkaNote.EPLJackpot,
				type: view == ViewEnum.mars ? 2 : 1,
				action: breakpoint <= Breakpoint.sm ? 8 : 22
			});
		}

		// golden duel
		if (isGoldenDuelSupported(sport, leagueId)) {
			setKafkaNormal({
				note: KafkaNote.GoldenDuel,
				type: 1,
				action: 45
			});
		}
	}

	const handleVisitSingleMatch = (event: React.MouseEvent) => {
		setPromotionKafka();
		router.push(singleMatchHref);
		// 避免桌機大小點開streamer 後進single match，iframe 變小，但plugin url 尚未取得新的會有破版情況
		streamingPluginUrl && streamingType && setStreamingPluginUrl('')
		setStreamingStatus('', '', []);
	}

	return (
		<div className="match__event" onClick={handleVisitSingleMatch}>
			{children}
		</div>
	)
}